/* eslint-disable new-cap,camelcase */
/* eslint-disable no-undef,no-use-before-define,no-param-reassign */
import {
    alphaToEmu,
    angleToEmu,
    canvasToEmu,
    ptToEmu,
    opacityToAlpha,
    numToPercent,
    percentToEmu,
    pxToPt,
    PERCENT_LINE_HEIGHT_SLIDE_TO_PPT_RATIO,
} from '@src/slide/infrastructure/module/independent/unit';
import expect from '@src/slide/infrastructure/module/independent/assert/expect';
import { SolidFill } from '@src/slide/domain/ui-model/plugin/referModels/FillClass';

import _set from 'lodash/set';
import _has from 'lodash/has';
import _get from 'lodash/get';
import { color2HexAndAlpha } from '@src/slide/infrastructure/module/independent/color/transform';
import { transMailObj2Str } from '@src/slide/domain/ui-model/element-modeles/property/link/ui-sdk-transform';
import { TEXT_BASELINE_OFFSET } from '@src/slide/domain/ui-model/element-modeles/property/text/constants';

/**
 * 从url(trueUrl)中去除trueUrl
 * @param {*} str 'url(trueUrl)'
 * @return {string} trueUrl
 */
export function getUrl(str) {
    const match = str.match(/^url\((.*)\)$/);
    if (match) return match[1];
    return '';
}

export function addPosition(ui, sdk) {
    ['top', 'left', 'width', 'height'].forEach((key) => {
        if (_has(ui, key)) _set(sdk, key, canvasToEmu(ui[key]));
    });
}

export function addTransform(ui, sdk, sdkPath) {
    if (_has(ui, 'rotate')) {
        _set(sdk, `${sdkPath}.rotate`, ui.rotate * 60000);
    }
    if (_has(ui, 'flipH')) {
        _set(sdk, `${sdkPath}.flipH`, ui.flipH);
    }
    if (_has(ui, 'flipV')) {
        _set(sdk, `${sdkPath}.flipV`, ui.flipV);
    }
}

const addAlphaColor = (color) => {
    if (color.alpha) { // 非空且非0进入
        color.alpha = color.alpha === 1 ? null : Math.round(alphaToEmu(color.alpha));
    }
};

const addBgPattFill = (ui, sdk) => {
    if (_has(ui, 'fillBackground.pattFill')) {
        _set(sdk, 'pattFill', ui.fillBackground.pattFill);
    }
};

// eslint-disable-next-line complexity
export function addBackground(ui, sdk) {
    if (_has(ui, 'fillBackground.solidFill')) {
        let { solidFill } = ui.fillBackground;
        if (solidFill instanceof SolidFill || (solidFill?.toPropValue)) { // 多选和表格会拷贝数据，普通拷贝不会去复制原型
            // 新的类对象
            const { color } = solidFill.toPropValue().solidFill;
            // TODO, 这里为了兼容逻辑，对SolidFill多包装了一层，后续对fillBackground整体改造之后，需要调整。
            solidFill = { color };
        }
        // solidFill不为空情况下，对solidFill.color.alpha进行统一处理
        solidFill && addAlphaColor(solidFill.color);

        _set(sdk, 'solidFill', solidFill);
    }

    if (_has(ui, 'fillBackground.image')) {
        _set(sdk, 'image', getImage(ui.fillBackground));
    }

    if (_has(ui, 'fillBackground.gradFill')) {
        _set(sdk, 'gradFill', getGradualFill(ui.fillBackground.gradFill));
    }

    if (_has(ui, 'fillBackground.noFill')) {
        _set(sdk, 'noFill', ui.fillBackground.noFill);
    }

    if (_has(ui, 'fillBackground.grpFill')) {
        _set(sdk, 'grpFill', ui.fillBackground.grpFill);
    }

    if (_has(ui, 'fillBackground.shadeToTitle')) {
        _set(sdk, 'shadeToTitle', ui.fillBackground.shadeToTitle);
    }

    addBgPattFill(ui, sdk);

    if (ui.fillBackground === null) {
        // 兼容null的情况
        _set(sdk, 'noFill', 1);
    }
}

// eslint-disable-next-line complexity
export function convertScene3D(uiData, sdkData) {
    if (_has(uiData, 'scene3d.camera.rotation')) {
        const rotation = {
            x: angleToEmu(uiData.scene3d.camera.rotation.x ?? 0),
            y: angleToEmu(uiData.scene3d.camera.rotation.y ?? 0),
            z: angleToEmu(uiData.scene3d.camera.rotation.z ?? 0),
        };
        _set(sdkData, 'props.scene3d.camera.rotation', rotation);
    }
    if (_has(uiData, 'scene3d.camera.preset')) {
        _set(sdkData, 'props.scene3d.camera.preset', uiData.scene3d.camera.preset);
    }
    if (_has(uiData, 'scene3d.lightRig.rigPreset')) {
        _set(sdkData, 'props.scene3d.lightRig.rigPreset', uiData.scene3d.lightRig.rigPreset);
    }
    if (_has(uiData, 'scene3d.lightRig.rotation')) {
        const rotation = {
            x: angleToEmu(uiData.scene3d.lightRig.rotation.x ?? 0),
            y: angleToEmu(uiData.scene3d.lightRig.rotation.y ?? 0),
            z: angleToEmu(uiData.scene3d.lightRig.rotation.z ?? 0),
        };
        _set(sdkData, 'props.scene3d.lightRig.rotation', rotation);
    }
}

// eslint-disable-next-line complexity
export function convertShape3D(uiData, sdkData) {
    if (_has(uiData, 'shape3d.extrusionHeight')) {
        _set(sdkData, 'props.shape3d.extrusionHeight', ptToEmu(uiData.shape3d.extrusionHeight));
    }
    if (_has(uiData, 'shape3d.extrusionColor')) {
        _set(sdkData, 'props.shape3d.extrusionColor', { ...uiData.shape3d.extrusionColor });
    }
    if (_has(uiData, 'shape3d.materialPreset')) {
        _set(sdkData, 'props.shape3d.materialPreset', uiData.shape3d.materialPreset);
    }
    if (_has(uiData, 'shape3d.bevelTop')) {
        const bevelTop = {
            width: ptToEmu(uiData.shape3d.bevelTop.width ?? 0),
            height: ptToEmu(uiData.shape3d.bevelTop.height ?? 0),
        };
        _set(sdkData, 'props.shape3d.bevelTop', bevelTop);
    }
    if (_has(uiData, 'shape3d.bevelBottom')) {
        const bevelBottom = {
            width: ptToEmu(uiData.shape3d.bevelBottom.width ?? 0),
            height: ptToEmu(uiData.shape3d.bevelBottom.height ?? 0),
        };
        _set(sdkData, 'props.shape3d.bevelBottom', bevelBottom);
    }
}

const convertImageTile = (tile) => {
    tile.tx = tile.tx ? canvasToEmu(tile.tx) : 0; // 图片偏移量
    tile.ty = tile.ty ? canvasToEmu(tile.ty) : 0;
    tile.sx = percentToEmu(tile.sx ?? 100);  // 图片百分比
    tile.sy = percentToEmu(tile.sy ?? 100);
    tile.flip = tile.flip || 'none'; // 图片镜像，值有 none,xy,x,y
    tile.algn = tile.algn || 'tl';  // 对齐方式，tl,tr,bl,br,t,l,ctr,r,b
    return tile;
};

function getImage(ui) {
    if (_has(ui, 'image.stretch.fillRect')) {
        const fillRect: any = {};
        fillRect.b = ui.image.stretch.fillRect.b * 100000 * -1;
        fillRect.t = ui.image.stretch.fillRect.t * 100000 * -1;
        fillRect.l = ui.image.stretch.fillRect.l * 100000 * -1;
        fillRect.r = ui.image.stretch.fillRect.r * 100000 * -1;
        const stretch = { fillRect };
        _set(ui.image, 'stretch', stretch);
    }

    if (_has(ui, 'image.alpha')) {
        const { alpha } = ui.image;
        const transAlpha = alpha ? Math.round(alphaToEmu(alpha)) : alpha;
        _set(ui.image, 'blip.alphaModFix', transAlpha);
        delete ui.image.alpha;
    }

    // image.src转换为image.blip.src
    if (_has(ui, 'image.src')) {
        _set(ui.image, 'blip.src', ui.image.src ? ui.image.src : null);
        if (_has(ui.image, 'src')) {
            delete ui.image.src;
        }
    }

    // 如果有值才去做转换。否则不用。
    if (_get(ui, 'image.tile')) {
        const { tile } = ui.image;
        convertImageTile(tile);
    }

    return ui.image;
}

function getPattFill(ui) {
    if (_has(ui, 'pattFill')) {
        const { pattFill } = ui;
        const sdkPattFillData: any = {};
        if (_has(pattFill, 'preset')) {
            sdkPattFillData.preset = pattFill.preset;
        }
        if (_has(pattFill, 'bgColor.color')) {
            sdkPattFillData.bgColor = pattFill.bgColor;
            delete sdkPattFillData.bgColor.color?.srgb_color;
        }
        if (_has(pattFill, 'fgColor.color')) {
            sdkPattFillData.fgColor = pattFill.fgColor;
            delete sdkPattFillData.fgColor.color?.srgb_color;
        }
        return sdkPattFillData;
    }
}

/**
 * 检测渐变点的pos的合法性
 * @param pos
 * @param index
 * @param count
 */
function checkGradualPointPos(pos, index, count) {
    let newPos = pos;
    if (pos < 0 || pos > 100) { // 异常数据
        if (index === 0) {
            newPos = 0;
        } else if (index === count - 1) {
            newPos = 100;
        } else {
            newPos = pos > 100 ? 100 : 0;
        }
    }
    return newPos;
}

/**
 * 检测渐变填充角度的合法性
 * @param ang
 */
function checkGradualAng(ang) {
    let newAng = ang;
    if (ang < 0 || ang >= 360) {
        newAng = 0;
    }
    return newAng;
}

function handlePathInvalid(gradFill) {
    if (_get(gradFill, 'pathGradient.path') === 'linear') {
        delete gradFill.pathGradient;
        if (!_has(gradFill, 'lin')) {
            gradFill.lin = { ang: 135 };  // UI默认值是135
        }
    }
}

function getGradualFill(gradFill) {
    if (gradFill) {
        if (Array.isArray(gradFill.gsList)) {
            const count = gradFill.gsList.length;
            gradFill.gsList.forEach((item, index) => {
                if (item?.color) {
                    item.pos = percentToEmu(checkGradualPointPos(item.pos, index, count));

                    const pColor = item.color.srgb_color;
                    const cv = color2HexAndAlpha(pColor);

                    item.color = {
                        colorType: 'rgb',
                        colorValue: cv.colorValue,
                        alpha: cv.alpha,
                        srgb_color: pColor,
                    };

                    if (item.color.alpha) {  // 非空且非0进入
                        item.color.alpha = item.color.alpha === 1
                            ? null : Math.round(alphaToEmu(item.color.alpha.toFixed(2)));
                    }
                    if (!_has(item.color, 'lumMod')) {
                        item.color.lumMod = null;
                    }
                    if (!_has(item.color, 'lumOff')) {
                        item.color.lumOff = null;
                    }
                    if (!_has(item.color, 'shade')) {
                        item.color.shade = null;
                    }
                }
            });
        }
        if (_get(gradFill, 'pathGradient.fillToRect')) {
            // 坐标转换
            const rect = gradFill.pathGradient.fillToRect;
            rect.b = percentToEmu(rect.b);
            rect.l = percentToEmu(rect.l);
            rect.r = percentToEmu(rect.r);
            rect.t = percentToEmu(rect.t);
        }
        // 做一下保护，因为linear为错误数据，这里要移除掉
        handlePathInvalid(gradFill);

        if (_get(gradFill, 'lin')) {
            // 角度转换
            if (gradFill.lin.ang === 360) { // 如果是360度，改成0，导出必须小于360
                gradFill.lin.ang = 0;
            }
            gradFill.lin.ang = angleToEmu(checkGradualAng(gradFill.lin.ang));
        }
        if (_get(gradFill, 'tileRect')) {
            // 坐标转换
            const rect = gradFill.tileRect;
            rect.b = percentToEmu(rect.b);
            rect.l = percentToEmu(rect.l);
            rect.r = percentToEmu(rect.r);
            rect.t = percentToEmu(rect.t);
        }
    }
    return gradFill;
}

export function addCustomShape(ui, sdk) {
    _set(sdk, 'bgShape', 'custom');
    _set(sdk, 'customGeom', ui);
}

export function addBgShape(ui, sdk) {
    if (_has(ui, 'bgShape')) {
        _set(sdk, 'prstGeomType', ui.bgShape);
    }
    if (_has(ui, 'controlValues')) {
        const adjList: any[] = [];
        for (let i = 0; i < ui.controlValues.length; i++) {
            adjList.push(`val ${ui.controlValues[i] * 100000}`);
        }
        _set(sdk, 'adjList', adjList);
    }
}

export function addBgLine(ui, sdk) {
    if (_has(ui, 'bgLine')) {
        _set(sdk, 'prstGeomType', ui.bgLine);
    }
    if (_has(ui, 'controlValues') && ui.controlValues) {
        const adjList: any[] = [];
        for (let i = 0; i < ui.controlValues.length; i++) {
            adjList.push(`val ${Math.round(ui.controlValues[i] * 100000)}`); // 转换完之后取个整，sdk要json压缩数据量
        }
        _set(sdk, 'adjList', adjList);
    }
}

export function addConnectorInfo(ui, sdk) {
    if (_has(ui, 'start')) {
        _set(sdk, 'stCxn', {});
    }
    if (_has(ui, 'end')) {
        _set(sdk, 'endCxn', {});
    }
    if (_has(ui, 'start.id')) {
        _set(sdk, 'stCxn.id', ui.start.id.toString());
    }
    if (_has(ui, 'start.index')) {
        _set(sdk, 'stCxn.index', ui.start.index);
    }
    if (_has(ui, 'end.id')) {
        _set(sdk, 'endCxn.id', ui.end.id.toString());
    }
    if (_has(ui, 'end.index')) {
        _set(sdk, 'endCxn.index', ui.end.index);
    }
}

const convertBorderColor = (sdk, ui, isTextBorder = false) => {
    if (ui.border.color) {
        let solidFill;
        if (typeof ui.border.color === 'object') {  // 可以外面构造一个solid fill
            solidFill = {
                color: {
                    lumMod: null,
                    lumOff: null,
                    shade: null,
                    alpha: null,
                    ...ui.border.color,
                },
            };
        } else {
            const cv: any = color2HexAndAlpha(ui.border.color);
            cv.alpha = cv.alpha === 1 ? null : Math.round(alphaToEmu(cv.alpha.toFixed(2))); // 等于1不行，SDK转换不对
            solidFill = {
                color: {
                    colorType: 'rgb',
                    colorValue: cv.colorValue,
                    alpha: cv.alpha,
                    lumMod: null,
                    lumOff: null,
                    shade: null,
                },
            };

            /* 解决这个问题：复制带边框的文字 ==> 创建新的文本框 ==> 粘贴，粘贴的文字没有边框颜色丢失的问题。
            引起这个问题的原因：
                在原有 sdk-ui 逻辑中，边框颜色取的是 srgb_color 字段，这个字段没有在 sdk 数据中保存，而是在 sdk 中动态计算得到，
                经过排查这个计算的逻辑应该是在 base-model.js 中 sdkData.getDataByInheritedProperties() 执行，但是在上述场景中不会触发这个逻辑，
                所以复制粘贴之后的数据没有 srgb_color 字段，导致边框颜色丢失。
                文字颜色和文字高亮也有这个问题，在当前文件中搜索 srgb_color 能看到其他的也是这样的解决方案。 */
            if (isTextBorder) {
                _set(solidFill, 'color.srgb_color', ui.border.color);
            }
        }
        _set(sdk, 'solidFill', solidFill);
    } else {
        _set(sdk, 'solidFill', null);
    }
};

// eslint-disable-next-line complexity
export function addBorder(ui, sdk, isTextBorder = false) {
    if (_has(ui, 'border.style')) {
        _set(sdk, 'style', ui.border.style);
    }
    if (_has(ui, 'border.width')) {
        _set(sdk, 'width', canvasToEmu(ui.border.width));
    }
    if (_has(ui, 'border.color')) {
        convertBorderColor(sdk, ui, isTextBorder);
    }
    if (_has(ui, 'border.gradFill')) {
        _set(sdk, 'gradFill', getGradualFill(ui.border.gradFill));
    }
    if (_has(ui, 'border.noFill')) {
        _set(sdk, 'noFill', ui.border.noFill);
    }
    if (_has(ui, 'border.linecap')) {
        _set(sdk, 'cap', ui.border.linecap);
    }

    if (_has(ui, 'border.linejoin')) {
        _set(sdk, 'lineJoin', ui.border.linejoin);
    }
    if (_has(ui, 'border.tailEnd')) {
        if (_has(ui, 'border.tailEnd.lineEndType')) {
            _set(sdk, 'tailEnd.lineEndType', ui.border.tailEnd.lineEndType);
        } else {     // TODO 后面需要改成removekey的方式，这种方式耦合了业务
            _set(sdk, 'tailEnd.lineEndType', null);
        }
        if (_has(ui, 'border.tailEnd.lengthType')) {
            _set(sdk, 'tailEnd.lengthType', ui.border.tailEnd.lengthType);
        } else {
            _set(sdk, 'tailEnd.lengthType', undefined);
        }
        if (_has(ui, 'border.tailEnd.widthType')) {
            _set(sdk, 'tailEnd.widthType', ui.border.tailEnd.widthType);
        } else {
            _set(sdk, 'tailEnd.widthType', undefined);
        }
    }
    if (_has(ui, 'border.headEnd')) {
        if (_has(ui, 'border.headEnd.lineEndType')) {
            _set(sdk, 'headEnd.lineEndType', ui.border.headEnd.lineEndType);
        } else {     // TODO 后面需要改成removekey的方式，这种方式耦合了业务
            _set(sdk, 'headEnd.lineEndType', null);
        }
        if (_has(ui, 'border.headEnd.lengthType')) {
            _set(sdk, 'headEnd.lengthType', ui.border.headEnd.lengthType);
        } else {
            _set(sdk, 'headEnd.lengthType', undefined);
        }
        if (_has(ui, 'border.headEnd.widthType')) {
            _set(sdk, 'headEnd.widthType', ui.border.headEnd.widthType);
        } else {
            _set(sdk, 'headEnd.widthType', undefined);
        }
    }
    if (_has(ui, 'border.sketchStyle')) {
        if (!expect(ui.border?.sketchStyle).isNullOrUndefined()) {
            if (_has(ui, 'border.sketchStyle.bgShape')) {
                if (_get(ui, 'border.sketchStyle.bgShape') === 'custom'
                    && _has(ui, 'border.sketchStyle.customGeom')
                ) {
                    _set(sdk, 'sketchStyle', { });
                    addCustomShape(ui.border.sketchStyle.customGeom, sdk.sketchStyle);
                } else {
                    _set(sdk, 'sketchStyle.prstGeom', { });
                    addBgShape(ui.border.sketchStyle, sdk.sketchStyle.prstGeom);
                }
            }
            if (_has(ui, 'border.sketchStyle.type')) {
                _set(sdk, 'sketchStyle.type', ui.border.sketchStyle.type);
            }
        } else {
            _set(sdk, 'sketchStyle', null);
        }
    }
    if (_has(ui, 'border.compoundType')) {
        _set(sdk, 'cmpd', ui.border.compoundType);
    }
}

const convertEffectShadow = (ui, sdk) => {
    if (ui.effect.shadow === null) { /** 要删除阴影 */
        if (!_has(sdk, 'outerShadow')) {
            _set(sdk, 'outerShadow', {});
        }
        if (!_has(sdk, 'innerShadow')) {
            _set(sdk, 'innerShadow', {});
        }
    } else { /** 要替换另一种阴影 */
        const shadowType = ui.effect.shadow.isOuter ? 'outerShadow' : 'innerShadow';
        if (!_has(sdk, shadowType)) {
            _set(sdk, shadowType, {});
        }
        addShadow(ui.effect.shadow, sdk[shadowType]);
    }
};

export function addEffect(ui, sdk) {
    if (_has(ui, 'effect.shadow')) {
        convertEffectShadow(ui, sdk);
    }

    if (_has(ui, 'effect.glow')) {
        if (!_has(sdk, 'glow')) {
            _set(sdk, 'glow', {});
        }
        addGlow(ui.effect.glow, sdk.glow);
    }

    if (_has(ui, 'effect.softEdge')) {
        if (!_has(sdk, 'softEdge')) {
            _set(sdk, 'softEdge', {});
        }
        addSoftEdge(ui.effect.softEdge, sdk.softEdge);
    }

    if (_has(ui, 'effect.reflection')) {
        if (!_has(sdk, 'reflection')) {
            _set(sdk, 'reflection', {});
        }
        addReflection(ui.effect.reflection, sdk.reflection);
    }
}
// 用于单元测试
export function addShadowAlgn(ui, sdk) {
    if (_has(ui, 'algn')) {
        _set(sdk, 'algn', ui.algn);
    }
}
// 用于单元测试
export function addShadowBlurRad(ui, sdk) {
    if (_has(ui, 'blurRad')) {
        _set(sdk, 'blurRad', ptToEmu(ui.blurRad));
    }
}
// 用于单元测试
export function addShadowDir(ui, sdk) {
    if (_has(ui, 'dir')) {
        _set(sdk, 'dir', angleToEmu(ui.dir));
    }
}
// 用于单元测试
export function addShadowDist(ui, sdk) {
    if (_has(ui, 'dist')) {
        _set(sdk, 'dist', ptToEmu(ui.dist));
    }
}
// 用于单元测试
export function addShadowRotWithShape(ui, sdk) {
    if (_has(ui, 'rotWithShape')) {
        _set(sdk, 'rotWithShape', ui.rotWithShape);
    }
}
// 用于单元测试
export function addShadowOpacity(ui, sdk) {
    if (_has(ui, 'opacity')) {
        _set(sdk, 'shadowColor.color.alpha', alphaToEmu(opacityToAlpha(ui.opacity)));
    }
}
// 用于单元测试
export function addShadowColor(ui, sdk) {
    if (_has(ui, 'color')) {
        const colorValue = ui.color.slice(1); /** 去掉# */
        _set(sdk, 'shadowColor.color.colorType', 'rgb');
        _set(sdk, 'shadowColor.color.colorValue', colorValue);
    }
}
// 用于单元测试
export function addShadowSize(ui, sdk) {
    const size = _get(ui, 'size');
    const sizeX = _get(ui, 'sizeX') || size;
    if (typeof sizeX === 'number') {
        _set(sdk, 'sx', percentToEmu(sizeX));
    }
    const sizeY = _get(ui, 'sizeY') || size;
    if (typeof sizeY === 'number') {
        _set(sdk, 'sy', percentToEmu(sizeY));
    }
}
// 用于单元测试
export function addShadowSkew(ui, sdk) {
    if (_has(ui, 'skewX')) {
        _set(sdk, 'kx', angleToEmu(ui.skewX));
    }
    if (_has(ui, 'skewY')) {
        _set(sdk, 'ky', angleToEmu(ui.skewY));
    }
}
export function addShadow(ui, sdk) {
    addShadowAlgn(ui, sdk);
    addShadowBlurRad(ui, sdk);
    addShadowDir(ui, sdk);
    addShadowDist(ui, sdk);
    addShadowRotWithShape(ui, sdk);
    addShadowOpacity(ui, sdk);
    addShadowColor(ui, sdk);
    addShadowSize(ui, sdk);
    addShadowSkew(ui, sdk);
    if (Object.keys(sdk).length === 0) return; /** ui里面只有isOuter属性，说明是从sdk删除shadow */
    _set(sdk, 'isOuter', ui.isOuter);
}
function addGlow(ui, sdk) {
    if (_has(ui, 'opacity')) {
        _set(sdk, 'glowColor.color.alpha', alphaToEmu(opacityToAlpha(ui.opacity)));
    }

    if (_has(ui, 'color')) {
        const colorValue = ui.color.slice(1); /** 去掉# */
        _set(sdk, 'glowColor.color.colorType', 'rgb');
        _set(sdk, 'glowColor.color.colorValue', colorValue);
    }

    if (_has(ui, 'size')) {
        _set(sdk, 'rad', ptToEmu(ui.size));
    }
}
function addSoftEdge(ui, sdk) {
    if (_has(ui, 'size')) {
        _set(sdk, 'rad', ptToEmu(ui.size));
    }
}
export function addReflection(ui, sdk) {
    if (_has(ui, 'algn')) {
        _set(sdk, 'algn', ui.algn); /** 对齐方式很有可能是预设。。todo by rick */
    }
    if (_has(ui, 'blurRad')) {
        _set(sdk, 'blurRad', ptToEmu(ui.blurRad));
    }
    if (_has(ui, 'dir')) {
        _set(sdk, 'dir', angleToEmu(ui.dir));
    }
    if (_has(ui, 'dist')) {
        _set(sdk, 'dist', ptToEmu(ui.dist));
    }
    if (_has(ui, 'rotWithShape')) {
        _set(sdk, 'rotWithShape', ui.rotWithShape);
    }
    if (_has(ui, 'endOpacity')) {
        _set(sdk, 'endA', Math.round(alphaToEmu(opacityToAlpha(ui.endOpacity))));
    }
    if (_has(ui, 'size')) {
        _set(sdk, 'endPos', percentToEmu(ui.size));
    }
    if (_has(ui, 'startOpacity')) {
        _set(sdk, 'stA', Math.round(alphaToEmu(opacityToAlpha(ui.startOpacity))));
    }
    if (_has(ui, 'sy')) {
        _set(sdk, 'sy', ui.sy * 100000); // 就是-1吧？？
    }
}

export function addColor(ui, sdk) {
    if (_has(ui, 'color')) {
        _set(sdk, 'solidFill.color.srgb_color', ui.color);
    }
}

export function addFont(ui, sdk) {
    if (_has(ui, 'fontFamily')) {
        _set(sdk, 'font.ea.typeface', ui.fontFamily);
    }

    if (_has(ui, 'fontSize')) {
        _set(sdk, 'font_size', ui.fontSize * 100);
    }
}

export function addUnderline(ui, sdk) {
    function convert(type) {
        switch (type) {
            case 'underline':
                return 'sng';
            default:
                return null;
        }
    }

    if (_has(ui, 'textDecoration')) {
        _set(sdk, 'underline', convert(ui.textDecoration));
    }
}

export function addVerticalAlign(ui, sdk) {
    if (ui.display === 'flex' && ui.alignItems) {
        switch (ui.display) {
            case 'flex-start':
                sdk.anchor = 't';
                break;
            case 'flex-end':
                sdk.anchor = 'b';
                break;
            case 'center':
                sdk.alignItems = 'ctr';
                break;
            default:
                break;
        }
    }

    if (ui.justifyContent) {
        switch (ui.justifyContent) {
            case 'start':
                sdk.anchorCtr = 0;
                break;
            case 'center':
                sdk.anchorCtr = 1;
                break;
            default:
                break;
        }
    }
}

export function convertFontWeight(ui, useStyle) {
    const style = useStyle;
    if (_has(ui, 'fontWeight')) {
        switch (ui.fontWeight) {
            case 'bold':
                _set(style, 'bold', 1);
                break;
            case 'thick':
                _set(style, 'thick', 1);
                break;
            case 'normal':
                _set(style, 'bold', 0);
                break;
            default:
                break;
        }
    }
}

export function convertFontStyle(ui, style) {
    if (_has(ui, 'fontStyle')) {
        switch (ui.fontStyle) {
            case 'italic':
                _set(style, 'italic', 1);
                break;
            case 'normal':
                _set(style, 'italic', 0);
                break;
            default:
                break;
        }
    }
}

export function convertFont(ui, style: any = {}) {
    if (_has(ui, 'fonts')) {
        _set(
            style,
            'font',
            Object.keys(ui.fonts)
                .reduce((prev, curr) => ({
                    ...prev,
                    [curr]: {
                        typeface: ui.fonts[curr],
                    },
                }), {}),
        );
    }

    if (_has(ui, 'lang')) {
        switch (ui.lang) {
            case 'latin':
                style.lang = 'en-US';
                break;
            case 'ea':
                style.lang = 'zh-CN';
                break;
            default:
                style.lang = ui.lang;
                break;
        }
    }

    if (_has(ui, 'fontSize')) {
        _set(style, 'font_size', ui.fontSize * 100);
    }

    convertFontWeight(ui, style);

    convertFontStyle(ui, style);

    return style;
}


export function convertColor(ui, style = {}) {
    if (_has(ui, 'color')) {
        // 颜色设置回去的时候都认为是来源于rgb而非theme，如果有疑问请咨询rongodzhang
        // _set(color, 'solidFill.color.colorType', 'rgb');
        // _set(color, 'solidFill.color.colorValue', ui.color.charAt(0) === '#' && ui.color.slice(1) || ui.color);

        if (ui.color) {
            let solidFill;
            if (typeof ui.color === 'object') {  // 可以外面构造一个solid fill
                solidFill = {
                    color: {
                        lumMod: null,
                        lumOff: null,
                        shade: null,
                        alpha: null,
                        ...ui.color,
                    },
                };
            } else {
                const cv: any = color2HexAndAlpha(ui.color);
                cv.alpha = cv.alpha === 1 ? null : Math.round(alphaToEmu(cv.alpha));
                solidFill = {
                    color: {
                        colorType: 'rgb',
                        colorValue: cv.colorValue,
                        alpha: cv.alpha,
                        lumMod: null,
                        lumOff: null,
                        shade: null,
                        srgb_color: ui.color, // 这里是为了处理复制粘贴报错，老代码挪过来。这里逻辑有问题，后面要调整。
                    },
                };
            }
            _set(style, 'solidFill', solidFill);
        } else {
            _set(style, 'solidFill', null);
        }
    }

    return style;
}

export function convertTextEffectList(ui, sdk: any = {}) {
    if (ui.effect) {
        if (!_has(sdk, 'effectList')) {
            _set(sdk, 'effectList', {});
        }
        addEffect(ui, sdk.effectList);
    }
    return sdk;
}

export const unitTestObj = {
    addBorder,
};

export function convertTextStroke(ui, sdk: any = {}) {
    if (ui?.border) {
        if (!_has(sdk, 'outline')) {
            _set(sdk, 'outline', {});
        }
        unitTestObj.addBorder(ui, sdk.outline, true);
    }
    return sdk;
}

// textbody的对齐方式，返回在props上
export function convertTextBodyAlign(ui, style: any = {}) {
    if (ui.wrapperStyle?.alignItems) {
        switch (ui.wrapperStyle.alignItems) {
            case 'flex-start':
                style.anchor = 't';
                break;
            case 'flex-end':
                style.anchor = 'b';
                break;
            case 'center':
                style.anchor = 'ctr';
                break;
            default:
                break;
        }
    }

    if (ui.bodyStyle?.width) {
        switch (ui.bodyStyle.width) {
            case '100%':
                style.anchorCtr = 0;
                break;
            case 'fit-content':
                style.anchorCtr = 1;
                break;
            default:
                break;
        }
    }

    return style;
}

export function convertFlatText(ui, style: any = {}) {
    if (typeof ui.bodyStyle?.isFlatText === 'boolean') {
        style.isFlatText = ui.bodyStyle.isFlatText ? 1 : 0;
    }
    return style;
}

export function convertTextAlign(ui, style: any = {}) {
    switch (ui.textAlign) {
        case 'left':
            style.alignment = 'l';
            break;
        case 'center':
            style.alignment = 'ctr';
            break;
        case 'right':
            style.alignment = 'r';
            break;
        case 'justify':
            style.alignment = 'just';
            break;
        case 'distribute':
            style.alignment = 'dist';
            break;
        default:
            break;
    }

    return style;
}

export function convertUnderline(ui, style: any = {}) {
    function convert(type) {
        switch (type) {
            case 'underline':
                return 'sng';
            default:
                return null;
        }
    }

    if (_has(ui, 'textDecoration')) {
        style.underline = convert(ui.textDecoration);
    }

    return style;
}

export function convertLetterSpace(ui, style: any = {}) {
    if (_has(ui, 'letterSpacing')) {
        style.spc = ui.letterSpacing * 100;
    }

    return style;
}

const convertLineHeight = (ui, style) => {
    switch (ui.lineHeightIs) {
        case 'absolute':
            _set(style, 'lnSpc.spcPts', ui.lineHeight * 100);
            break;
        case 'percent':
        default:
            if (_has(ui, 'PPTLineHeight') && (ui.PPTLineHeight !== null)) {
                _set(style, 'lnSpc.spcPct', numToPercent(ui.PPTLineHeight));
            } else {
                _set(style, 'lnSpc.spcPct', numToPercent(ui.lineHeight) / PERCENT_LINE_HEIGHT_SLIDE_TO_PPT_RATIO);
            }
            break;
    }
};

export function convertLineSpace(ui, style = {}) {
    if (_has(ui, 'paddingTop')) {
        switch (ui.paddingTopIs) {
            case 'percent':
                _set(style, 'spcBef.spcPct', numToPercent(parseFloat(ui.paddingTop)));
                break;
            case 'absolute':
            default:
                _set(style, 'spcBef.spcPts', parseFloat(ui.paddingTop) * 100);
                break;
        }
    }

    if (_has(ui, 'paddingBottom')) {
        switch (ui.paddingBottomIs) {
            case 'percent':
                _set(style, 'spcAft.spcPct', numToPercent(parseFloat(ui.paddingBottom)));
                break;
            case 'absolute':
            default:
                _set(style, 'spcAft.spcPts', parseFloat(ui.paddingBottom) * 100);
                break;
        }
    }

    if (_has(ui, 'lineHeight')) {
        convertLineHeight(ui, style);
    }

    return style;
}

// 文字背景色
export function convertHighlight(ui, style = {}) {
    if (_has(ui, 'backgroundColor')) {
        if (!ui.backgroundColor) {  // 设置无背景颜色
            _set(style, 'highlight', null);
        } else {
            // _set(hl, 'highlight.color.colorType', 'rgb');
            // _set(hl, 'highlight.color.colorValue', ui.backgroundColor.charAt(0)
            // === '#' && ui.backgroundColor.slice(1) || ui.backgroundColor);

            const cv: any = color2HexAndAlpha(ui.backgroundColor);
            cv.alpha = cv.alpha === 1 ? null : Math.round(alphaToEmu(cv.alpha));
            const highlight = {
                color: {
                    colorType: 'rgb',
                    colorValue: cv.colorValue,
                    alpha: cv.alpha,
                    lumMod: null,
                    lumOff: null,
                    shade: null,
                },
            };
            _set(style, 'highlight', highlight);

            _set(style, 'highlight.color.srgb_color', ui.backgroundColor);
        }
    }

    return style;
}

export function convertWrap(ui, style: any = {}) {
    switch (_get(ui, 'bodyStyle.whiteSpace')) {
        case 'pre':
            style.wrap = 'none';
            break;
        case 'pre-wrap':
            style.wrap = 'square';
            break;
        default:
            break;
    }

    return style;
}

export function convertPosition(ui, style: any = {}) {
    if (_has(ui, 'marginLeft')) {
        style.marginLeft = ptToEmu(ui.marginLeft);
    }

    return style;
}

export function convertAutoFit(ui, style: any = {}) {
    if (ui.autoFitType) {
        style.autoFitType = ui.autoFitType;
    }

    return style;
}

function convertFontScale(ui, style: any = {}) {
    if (ui.wrapperStyle?.fontScale) {
        style.fontScale = numToPercent(ui.wrapperStyle.fontScale);
    }

    return style;
}

// 这玩意不是一个css style
export function convertStrike(ui, style: any = {}) {
    function convert(type) {
        switch (type) {
            case 'sng':
                return 'sngStrike';
            case 'dbl':
                return 'dblStrike';
            case 'none':
                return 'noStrike';
            default:
                return null;
        }
    }

    if (_has(ui, 'del')) {
        style.strike = convert(ui.del);
    }

    return style;
}

// 这玩意不是一个css style
export function convertTab(ui, style: any = {}) {
    if (_has(ui, 'tabSize')) {
        // @ts-ignore
        style.defTablSz = parseInt(ptToEmu(ui.tabSize), 10);
    }

    return style;
}

export function convertTabList(ui, style = {}) {
    if (_has(ui, 'tabList')) {
        const alignmentMapping = {
            left: 'l',
            right: 'r',
            center: 'ctr',
            decimal: 'dec',
        };
        const sdkTabList = ui.tabList.map(tabstop => ({
            pos: ptToEmu(tabstop.position),
            algn: alignmentMapping[tabstop.alignment],
        }));
        _set(style, 'tabList', sdkTabList);
    }
    return style;
}

export function convertIndent(ui, style: any = {}) {
    if (!expect(ui.textIndent).isNullOrUndefined()) {
        style.indent = ptToEmu(ui.textIndent);
    }
    return style;
}

function convertBuNone(ui, style: any = {}) {
    if (!expect(ui.buNone).isNullOrUndefined()) {
        style.buNone = ui.buNone;
    }
    return style;
}

export function convertLink(ui, style: any = {}) {
    if (_has(ui, 'href')) {
        const data: any = {};
        if (_has(ui.href, 'mail')) {
            data.url = transMailObj2Str(ui.href.mail);
        }
        if (_has(ui.href, 'url')) {
            data.url = ui.href.url;
        }
        if (_has(ui.href, 'slideJump')) {
            data.slideJump = ui.href.slideJump;
        }
        if (_has(ui.href, 'tooltip')) {
            data.tooltip = ui.href.tooltip;
        }
        if (_has(ui.href, 'colorType')) {
            data.colorType = ui.href.colorType;
        }
        if (_has(ui.href, 'mimeType')) {
            data.mimeType = ui.href.mimeType;
        }
        if (_has(ui.href, 'iconMode')) {
            data.iconMode = ui.href.iconMode;
        }
        style.hlink = data;
    } else {
        style.hlink = null;
    }
    return style;
}

export const buSelfTypeMap = {
    itemSymbol_circle: (style) => {
        style.character_bullet_char = 'l';
        style.buFont = {
            charset: '2',
            typeface: 'Wingdings',
        };
    },
    itemSymbol_square: (style) => {
        style.character_bullet_char = 'n';
        style.buFont = {
            charset: '2',
            typeface: 'Wingdings',
        };
    },
    itemSymbol_rhombus: (style) => {
        style.character_bullet_char = 'u';
        style.buFont = {
            charset: '2',
            typeface: 'Wingdings',
        };
    },
    itemSymbol_star: (style) => {
        style.buBlip = {};
    },
    itemSymbol_check: (style) => {
        style.character_bullet_char = 'ü';
        style.buFont = {
            charset: '2',
            typeface: 'Wingdings',
        };
    },
    itemSymbol_arrow: (style) => {
        style.character_bullet_char = 'Ø';
        style.buFont = {
            charset: '2',
            typeface: 'Wingdings',
        };
    },
    itemSymbol_small_circle: (style) => {
        style.character_bullet_char = '•';
        style.buFont = {
            charset: '0',
            typeface: 'Arial',
        };
    },
};

/**
 * 项目符号颜色转换
 * @param ui
 * @param style
 */
export function convertBuClr(ui, style) {
    if (ui.buClr) {
        style.buClr = ui.buClr;
        // lumMod 属性质空
        if (_get(ui, 'buClr.color.lumMod')) {
            style.buClr.color.lumMod = null;
        }
    }
}

const convertCharacterSz = (ui, style) => {
    if (ui.buSzPct) {
        style.buSzPct = numToPercent(ui.buSzPct);
    }

    if (ui.buSzPts) {
        style.buSzPts = pxToPt(ui.buSzPts);
    }
    if (ui.buSzTx) {
        style.buSzTx = ui.buSzTx;
    }
};

export function convertCharacter(ui, style: any = {}) {
    if (!ui) {
        return style;
    }
    if (!expect(ui.character_bullet_char).isNullOrUndefined()) {
        style.character_bullet_char = ui.character_bullet_char;
    }

    if (!expect(ui.buAutoNum).isNullOrUndefined()) {
        style.buAutoNum = ui.buAutoNum;
    }

    if (!expect(ui.buBlip).isNullOrUndefined()) {
        style.buBlip = ui.buBlip;
    }

    if (ui.buFont) {
        style.buFont = JSON.parse(JSON.stringify(ui.buFont));
    }

    // 项目符号color转换
    convertBuClr(ui, style);

    convertCharacterSz(ui, style);

    if (ui.buFontTx) {
        style.buFontTx = ui.buFontTx;
    }

    if (ui.buClrTx) {
        style.buClrTx = ui.buClrTx;
    }

    if (!expect(ui.buNone).isNullOrUndefined()) {
        style.buNone = ui.buNone;
    }

    return style;
}

export function convertAddon(ui, style: any = {}) {
    if (_has(ui, 'displayMode')) {
        style.displayMode = ui.displayMode;
    }
    if (_has(ui, 'embedUrl')) {
        style.embedUrl = ui.embedUrl;
    }
    if (_has(ui, 'iframeId')) {
        style.iframeId = ui.iframeId;
    }
    if (_has(ui, 'extraData')) {
        style.extraData = ui.extraData;
    }
    if (_has(ui, 'title')) {
        style.title = ui.title;
    }
    if (_has(ui, 'originUrl')) {
        style.originUrl = ui.originUrl;
    }
    if (_has(ui, 'aid')) {
        style.aid = ui.aid;
    }
    if (_has(ui, 'type')) {
        style.type = ui.type;
    }
    if (_has(ui, 'docElement')) {
        style.docElement = ui.docElement;
    }
    return style;
}

export function convertAttachment(ui, style: any = {}) {
    if (_has(ui, 'displayMode')) {
        style.displayMode = ui.displayMode;
    }
    if (_has(ui, 'id')) {
        style.id = ui.id;
    }
    if (_has(ui, 'name')) {
        style.name = ui.name;
    }
    if (_has(ui, 'objKey')) {
        style.objKey = ui.objKey;
    }
    if (_has(ui, 'type')) {
        style.type = ui.type;
    }
    if (_has(ui, 'classify')) {
        style.classify = ui.classify;
    }
    if (_has(ui, 'showType')) {
        style.showType = ui.showType;
    }
    if (_has(ui, 'size')) {
        style.size = ui.size;
    }
    if (_has(ui, 'posterInfo')) {
        style.posterInfo = ui.posterInfo;
    }
    return style;
}

export function convertTextFill(ui, style = {}) {
    if (ui.gradFill) {
        _set(style, 'gradFill', getGradualFill(ui.gradFill));
    }
    if (ui.image) {
        _set(style, 'image', getImage(ui));
    }
    if (ui.pattFill) {
        _set(style, 'pattFill', getPattFill(ui));
    }
    if (ui.noFill) {
        _set(style, 'noFill', ui.noFill);
    }
    return style;
}

function convertResizeLevel(ui, style: any = {}) {
    if (ui.wrapperStyle && !expect(ui.wrapperStyle.resizeLevel).isNullOrUndefined()) {
        style.resizeLevel = ui.wrapperStyle.resizeLevel;
    }

    return style;
}

export function convertPadding(ui, style: any = {}) {
    if (_has(ui, 'bodyStyle.paddingLeft')) {
        style.left_inset = ptToEmu(ui.bodyStyle.paddingLeft);
    }
    if (_has(ui, 'bodyStyle.paddingRight')) {
        style.right_inset = ptToEmu(ui.bodyStyle.paddingRight);
    }
    if (_has(ui, 'bodyStyle.paddingTop')) {
        style.top_inset = ptToEmu(ui.bodyStyle.paddingTop);
    }
    if (_has(ui, 'bodyStyle.paddingBottom')) {
        style.bottom_inset = ptToEmu(ui.bodyStyle.paddingBottom);
    }
    return style;
}

// 转换文本方向
export function convertTextDirection(ui, style: any = {}) {
    if (_has(ui, 'wrapperStyle.textDirection')) {
        style.vert = ui.wrapperStyle.textDirection;
    }
    return style;
}

// 转换分栏属性
export function convertColumnProps(ui, style: any = {}) {
    if (_has(ui, 'wrapperStyle.columnNumber')) {
        style.numCol = ui.wrapperStyle.columnNumber;
    }
    if (_has(ui, 'wrapperStyle.columnSpace')) {
        style.spcCol = ui.wrapperStyle.columnSpace;
    }
    return style;
}

export function convertBaseLineOffset(ui, style: any = {}) {
    if (_has(ui, TEXT_BASELINE_OFFSET)) {
        style.baseline = numToPercent(ui[TEXT_BASELINE_OFFSET]);
    }
    return style;
}

export function convertStyle(ui) {
    // 不采用函数式的方式了，参见原本的convert，deepMerge损耗过大
    return [
        convertTextBodyAlign,
        convertFlatText,
        convertTextAlign,
        convertFont,
        convertUnderline,
        convertPosition,
        convertStrike,
        convertTab,
        convertTabList,
        convertWrap,
        convertColor,
        convertLetterSpace,
        convertLineSpace,
        convertHighlight,
        convertAutoFit,
        convertIndent,
        convertBuNone,
        convertLink,
        convertAddon,
        convertTextFill,
        convertResizeLevel,
        convertFontScale,
        convertTextEffectList,
        convertPadding,
        convertTextStroke,
        convertTextDirection,
        convertColumnProps,
        convertBaseLineOffset,
    ].reduce((prev, curr) => curr(ui, prev), {});
}

// 用于单元测试
export const testTable = {
    handlePathInvalid,
    getImage,
    addBgPattFill,
    convertFontScale,
    getPattFill,
};
